import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Upload, SlidersHorizontal, X } from 'lucide-react';
import { useNotes } from '../hooks/useApi';
import { useSiteSettings } from '../hooks/useApi';
import { usePageTitle } from '../hooks/usePageTitle';
import { useNotesFiltersFromUrl } from '../hooks/useNotesFiltersFromUrl';
import PageShell from '../components/PageShell';
import NotesSearchHero from '../components/notes/NotesSearchHero';
import NotesFilterPanel from '../components/notes/NotesFilterPanel';
import NotesSortBar from '../components/notes/NotesSortBar';
import NotesActiveFilters from '../components/notes/NotesActiveFilters';
import NotesResultsGrid from '../components/notes/NotesResultsGrid';
import { filtersToApiParams, hasActiveFilters } from '../utils/notesFilterUtils';
import type { NotesFilters } from '../utils/notesFilterUtils';

const NotesBrowsePage: React.FC = () => {
  const { filters, setFilter, clearFilters, setPage } = useNotesFiltersFromUrl();
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const { settings } = useSiteSettings();

  usePageTitle(filters.q ? `Notlar: ${filters.q}` : 'Tüm Notlar');

  const apiParams = filtersToApiParams(filters);
  const { notes, pagination, loading, error } = useNotes(apiParams);

  const universities = settings?.universities?.length
    ? settings.universities
    : ['İstanbul Üniversitesi', 'ODTÜ', 'Boğaziçi Üniversitesi', 'Diğer'];
  const departments = settings?.departments?.length ? settings.departments : [];

  const totalPages = pagination?.pages ?? 1;
  const resultLabel = pagination
    ? `${pagination.total} sonuç · sayfa ${pagination.current}/${totalPages}`
    : loading
      ? 'Yükleniyor…'
      : '';

  const handleQueryCommit = useCallback(
    (q: string) => setFilter('q', q),
    [setFilter]
  );

  const handleRemoveFilter = useCallback(
    (key: keyof NotesFilters) => {
      if (key === 'sort') setFilter('sort', 'newest');
      else setFilter(key, '' as NotesFilters[typeof key]);
    },
    [setFilter]
  );

  return (
    <PageShell
      badge="Onaylı notlar"
      badgeIcon={BookOpen}
      badgeColor="#06b6d4"
      title="Not arşivi"
      subtitle="Ara, filtrele ve onaylı üniversite notlarına ulaş"
      headerExtra={
        <Link to="/notlar/yukle" className="btn-shimmer notes-upload-cta">
          <Upload size={16} />
          Not yükle
        </Link>
      }
    >
      <NotesSearchHero query={filters.q} onQueryCommit={handleQueryCommit} />

      <div className="notes-toolbar">
        <button
          type="button"
          className="notes-toolbar__filters-mobile"
          onClick={() => setMobileFiltersOpen(true)}
          aria-expanded={mobileFiltersOpen}
        >
          <SlidersHorizontal size={18} />
          Filtreler
          {hasActiveFilters(filters) ? <span className="notes-toolbar__badge" /> : null}
        </button>
        <div className="notes-toolbar__desktop">
          <NotesFilterPanel
            filters={filters}
            universities={universities}
            departments={departments}
            onChange={setFilter}
          />
        </div>
      </div>

      {mobileFiltersOpen && (
        <div className="notes-filter-sheet" role="dialog" aria-modal="true" aria-label="Filtreler">
          <div className="notes-filter-sheet__backdrop" onClick={() => setMobileFiltersOpen(false)} />
          <div className="notes-filter-sheet__panel">
            <div className="notes-filter-sheet__head">
              <h2>Filtreler</h2>
              <button type="button" onClick={() => setMobileFiltersOpen(false)} aria-label="Kapat">
                <X size={22} />
              </button>
            </div>
            <NotesFilterPanel
              filters={filters}
              universities={universities}
              departments={departments}
              onChange={(key, value) => {
                setFilter(key, value);
              }}
              idSuffix="-mobile"
            />
            <button type="button" className="notes-filter-sheet__apply" onClick={() => setMobileFiltersOpen(false)}>
              Sonuçları göster
            </button>
          </div>
        </div>
      )}

      <div className="notes-results-bar">
        <div className="notes-results-bar__meta">
          <span className="notes-results-bar__count">{resultLabel}</span>
          <NotesActiveFilters filters={filters} onRemove={handleRemoveFilter} onClearAll={clearFilters} />
        </div>
        <NotesSortBar sort={filters.sort} onChange={(sort) => setFilter('sort', sort)} />
      </div>

      <NotesResultsGrid
        notes={notes}
        loading={loading}
        error={error}
        pagination={pagination}
        page={filters.page}
        onPageChange={setPage}
        hasFilters={hasActiveFilters(filters)}
      />
    </PageShell>
  );
};

export default NotesBrowsePage;
